import { ProductDetailsResponse, ProductRole } from "@/@types/Products";
import { userInitials } from "@/lib/utils";
import i18n from "@/translations";
import { GitBranch } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { usePermissions } from "@/auth/PermissionsContext";
import Badge from "../shared/Badge/Badge";
import CustomSelect from "../shared/CustomSelect/CustomSelect";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Skeleton } from "../ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import ProductUuidBadge from "./product-uuid-badge";

interface ProductHeaderProps {
  product?: ProductDetailsResponse;
  isLoading?: boolean;
}

export default function EditProductHeader({
  product,
  isLoading,
}: ProductHeaderProps) {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { hasPermission, hasAnyPermission } = usePermissions();

  const tabList = useMemo(() => {
    if (!product) return [];

    return [
      {
        value: `/products/edit-product/${product.id}/geral`,
        label: t("geral"),
        route: `/products/edit-product/${product.id}/geral`,
        dataCy: "tab-geral",
        available: hasPermission('products_edit.view_product_details'),
      },
      {
        value: `/products/edit-product/${product.id}/offers`,
        label: t("offers"),
        route: `/products/edit-product/${product.id}/offers`,
        dataCy: "tab-offers",
        available: hasPermission('offers.view_offers'),
      },
      {
        value: `/products/edit-product/${product.id}/checkouts`,
        label: t("tabCheckouts"),
        route: `/products/edit-product/${product.id}/checkouts`,
        dataCy: "tab-checkouts",
        available: hasPermission('checkouts.view_checkouts'),
      },
      {
        value: `/products/edit-product/${product.id}/links`,
        label: t("tabLinks"),
        route: `/products/edit-product/${product.id}/links`,
        dataCy: "tab-links",
        available: hasPermission('links.view_links'),
      },
      {
        value: `/products/edit-product/${product.id}/pixels`,
        label: t("tabPixels"),
        route: `/products/edit-product/${product.id}/pixels`,
        dataCy: "tab-pixels",
        available: hasPermission('pixels.view_pixels'),
      },
      {
        value: `/products/edit-product/${product.id}/coupons`,
        label: t("tabCupons"),
        route: `/products/edit-product/${product.id}/coupons`,
        dataCy: "tab-coupons",
        // Não há action key de cupom na API; o vínculo com o produto já é o gate
        // da CouponPolicy, e criar/editar/excluir são restritos ao produtor na página.
        available: true,
      },
      {
        value: `/products/edit-product/${product.id}/coproductor`,
        label: t("tabCoProducer"),
        route: `/products/edit-product/${product.id}/coproductor`,
        dataCy: "tab-coproductor",
        available:
          product?.relationship?.role !== ProductRole.AFFILIATE &&
          hasPermission('coproducers.view_coproducers'),
      },
      {
        value: `/products/edit-product/${product.id}/affiliate`,
        label: t("tabAffiliate"),
        route: `/products/edit-product/${product.id}/affiliate`,
        dataCy: "tab-affiliate",
        available:
          product?.relationship?.role !== ProductRole.AFFILIATE &&
          hasPermission('affiliates.view_affiliates'),
      },
      {
        value: `/products/edit-product/${product.id}/public-marketplace`,
        label: t("tabPublicMarketplace"),
        route: `/products/edit-product/${product.id}/public-marketplace`,
        dataCy: "tab-public-marketplace",
        available:
          product?.relationship?.role === ProductRole.PRODUCER &&
          hasPermission('products_edit.view_product_details'),
      },
      {
        value: `/products/edit-product/${product.id}/partners`,
        label: t("tabPartners"),
        route: `/products/edit-product/${product.id}/partners`,
        dataCy: "tab-partners",
        available:
          product?.relationship?.role === ProductRole.PRODUCER &&
          hasAnyPermission([
            'partner_indications.configure_partner_program',
            'partner_indications.view_partner_indications',
            'partner_indications.manage_partners',
          ]),
      },
      {
        value: `/products/edit-product/${product.id}/order-bumps`,
        label: t("orderBumps"),
        route: `/products/edit-product/${product.id}/order-bumps`,
        dataCy: "tab-order-bumps",
        available:
          product?.relationship?.role !== ProductRole.AFFILIATE &&
          hasPermission('order_bumps.view_order_bumps'),
      },
      {
        value: `/products/edit-product/${product.id}/upsells`,
        label: t("upsells"),
        route: `/products/edit-product/${product.id}/upsells`,
        dataCy: "tab-upsells",
        available:
          product?.relationship?.role !== ProductRole.AFFILIATE &&
          hasPermission('upsells.view_upsells'),
      },
    ].filter((item) => item.available);
  }, [product, t, hasPermission]);

  const activeTab = useMemo(
    () => tabList.find((tab) => pathname === tab.route)?.value || "geral",
    [pathname, tabList]
  );

  if (isLoading) {
    return (
      <section className="space-y-8">
        <div className="flex flex-col items-center justify-center mt-8 space-y-3">
          <Skeleton className="h-14 w-14 rounded-full" />
          <div className="flex flex-col md:flex-row items-center gap-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-6 w-20" />
          </div>
        </div>
        <div className="md:hidden">
          <Skeleton className="h-10 w-full" />
        </div>
        <div className="hidden md:block">
          <Skeleton className="h-10 w-full max-w-[760px] mx-auto" />
        </div>
      </section>
    );
  }

  if (!product) return null;

  const shouldBreakToSelect = i18n.language === "pt" || i18n.language === "en";

  return (
    <section className="space-y-8">
      <div className="flex flex-col items-center justify-center mt-8 space-y-3">
        <Avatar className="h-14 w-14 bg-blackA1">
          <AvatarImage
            src={product.images?.[0]?.url}
            className="h-full w-full"
            alt={product.name}
            loading="lazy"
          />
          <AvatarFallback className="bg-elevated text-xl font-medium text-muted">
            {userInitials(product.name)}
          </AvatarFallback>
        </Avatar>
        {product?.relationship?.role !== ProductRole.AFFILIATE && (
          <button
            onClick={() => navigate(`/products/edit-product/${product.id}/funnel`)}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 border border-primary/20 px-3 py-1 text-[11px] font-medium text-primary hover:bg-primary/20 hover:border-primary/30 transition-all"
          >
            <GitBranch className="h-3 w-3" />
            {t('view_funnel', 'Ver Funil')}
          </button>
        )}
        <div className="flex flex-col md:flex-row items-center gap-2">
          <h1 className="font-semibold max-w-[530px] text-center text-2xl line-clamp-2">
            {product.name}
          </h1>
          <Badge status={product.status} />
        </div>
        <ProductUuidBadge uuid={product.id} variant="inline" />
      </div>

      <div className={shouldBreakToSelect ? "md:hidden" : "xl:hidden"}>
        <CustomSelect
          placeholder=""
          options={tabList}
          value={activeTab}
          onValueChange={navigate}
          disabled={isLoading}
        />
      </div>

      <div
        className={shouldBreakToSelect ? "hidden md:block" : "hidden xl:block"}
      >
        <div className="w-full max-w-full overflow-x-auto mt-8">
          <div className="flex w-max min-w-full justify-center">
            <Tabs value={activeTab} className="w-max">
              <TabsList className="!inline-flex w-max flex-nowrap !justify-start gap-1 bg-[#18181B] p-1 rounded-full">
                {tabList.map((tab) => (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    onClick={() => navigate(tab.route)}
                    data-cy={tab.dataCy}
                    className="shrink-0 px-3 py-1 text-sm rounded-full data-[state=active]:bg-[#27272A] data-[state=active]:text-white"
                  >
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
        </div>
      </div>
    </section>
  );
}
